import { createAction } from "@reduxjs/toolkit";
import { isEmpty } from "underscore";
import { EntryRightDelegeeDetails } from "../../api/entryRights.api";
import { DelegatedParkingRight } from "../../models/delegations/DelegatedParkingProductsModel";
import type { EntryRightsState } from "../reducers/entry-rights.reducer";
import type { ParkingProductState } from "../reducers/parking-products";

// Names from a finished bulk delegation, keyed by normalized email address.
// The delegee read model is updated asynchronously after the bulk reports 100%,
// so a refetch right after the bulk can still return the previous names.
export type DelegeeNameOverrides = Record<string, string>;

export const bulkDelegeeNamesUpdatedAction = createAction<DelegeeNameOverrides>("BULK_DELEGEE_NAMES_UPDATED");

type DelegeeWithEmailAddress = { name: string; emailAddress: string };
type DelegeeWithEmail = { name: string; email: string };

type PagesWithData<T> = { [page: number]: { data?: T[] | null } } | null | undefined;

export const normalizeEmail = (email?: string | null) => (email ?? "").trim().toLowerCase();

export function createDelegeeNameOverrides(delegees: { name: string; emailAddress: string }[]): DelegeeNameOverrides {
    const overrides: DelegeeNameOverrides = {};
    for (const delegee of delegees) {
        const email = normalizeEmail(delegee.emailAddress);
        const name = delegee.name?.trim();
        if (email && name) {
            overrides[email] = name;
        }
    }
    return overrides;
}

export function removeDelegeeNameOverride(overrides: DelegeeNameOverrides, email?: string | null) {
    delete overrides[normalizeEmail(email)];
}

function applyOverride(overrides: DelegeeNameOverrides, delegee: DelegeeWithEmailAddress | DelegeeWithEmail | null | undefined) {
    if (!delegee) {
        return;
    }
    const email = "emailAddress" in delegee ? delegee.emailAddress : delegee.email;
    const name = overrides[normalizeEmail(email)];
    if (name !== undefined && delegee.name !== name) {
        delegee.name = name;
    }
}

function applyOverrideToDetails(overrides: DelegeeNameOverrides, details: EntryRightDelegeeDetails | null | undefined) {
    if (!details) {
        return;
    }
    const name = overrides[normalizeEmail(details.delegeeEmail)];
    if (name !== undefined && details.delegeeName !== name) {
        details.delegeeName = name;
    }
}

function forEachItem<T>(pages: PagesWithData<T>, apply: (item: T) => void) {
    Object.values(pages ?? {}).forEach(page => page?.data?.forEach(apply));
}

export function applyDelegeeNameOverridesToParkingRights(
    overrides: DelegeeNameOverrides,
    parkingRights: DelegatedParkingRight[] | undefined
): DelegatedParkingRight[] | undefined {
    const name = (parkingRight: DelegatedParkingRight) => overrides[normalizeEmail(parkingRight.delegee.emailAddress)];
    return parkingRights?.map(parkingRight => name(parkingRight) === undefined
        ? parkingRight
        : { ...parkingRight, delegee: { ...parkingRight.delegee, name: name(parkingRight) } });
}

export function applyDelegeeNameOverridesToParkingProducts(state: ParkingProductState) {
    const overrides = state.bulkDelegeeNameOverrides;
    if (isEmpty(overrides)) {
        return;
    }
    forEachItem(state.delegees.pages, delegee => applyOverride(overrides, delegee));
    forEachItem(state.delegatedParkingProducts.pages, parkingRight => applyOverride(overrides, parkingRight.delegee));
    applyOverride(overrides, state.delegeeDetails.data);
}

export function applyDelegeeNameOverridesToEntryRights(state: EntryRightsState) {
    const overrides = state.bulkDelegeeNameOverrides;
    if (isEmpty(overrides)) {
        return;
    }
    forEachItem(state.myProducts.pedestrian.pages, entryRight => applyOverride(overrides, entryRight.delegee));
    forEachItem(state.myProducts.parking.pages, entryRight => applyOverride(overrides, entryRight.delegee));
    forEachItem(state.myFleet.delegees.pages, delegee => applyOverride(overrides, delegee));
    forEachItem(state.myFleetOverview.pedestrians.pages, delegee => applyOverride(overrides, delegee.delegee));
    forEachItem(state.myFleetOverview.parkingRights.pages, delegee => applyOverride(overrides, delegee.delegee));
    applyOverrideToDetails(overrides, state.delegeeDetails.data);
}
